import React, { useState, useEffect } from 'react';
import { ChevronLeft, Loader2, AlertTriangle, ExternalLink, Search } from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

interface TraceabilityScreenProps {
  onBack: () => void;
  title: string;
  t: any;
}

const TraceabilityScreen: React.FC<TraceabilityScreenProps> = ({ onBack, title, t }) => {
  const [isOpeningInApp, setIsOpeningInApp] = useState(false);
  const url = "https://traceability-ryss.ap.gov.in/apcnf.html";
  const isNative = Capacitor.isNativePlatform();

  const handleOpenLink = async () => {
    if (isNative) {
      try {
        setIsOpeningInApp(true);
        await Browser.open({ 
          url: url,
          windowName: '_blank',
          toolbarColor: '#1b5e20'
        });
      } catch (err) {
        console.error('Browser plugin error:', err);
        window.open(url, '_blank');
      } finally {
        setIsOpeningInApp(false);
      }
    } else {
      window.open(url, '_blank');
    }
  };

  // Auto-open if native on first load
  useEffect(() => {
    if (isNative) {
      handleOpenLink();
    }
  }, []);

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Header */}
      <div className="bg-[#1b5e20] text-white px-4 py-3 flex items-center gap-3 shadow-md z-10">
        <button 
          onClick={onBack}
          className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-95"
          aria-label="Back"
        >
          <ChevronLeft size={24} />
        </button>
        <div className="flex flex-col">
          <h1 className="text-sm font-medium opacity-80 leading-tight">Prakruthi Mithra</h1>
          <h2 className="text-lg font-bold leading-tight">{title}</h2>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 relative bg-stone-50 overflow-hidden flex flex-col items-center justify-center p-8 text-center">
        {isNative ? (
          <div className="space-y-6">
            <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-2">
              <Search size={48} className="text-[#1b7d36]" />
            </div>
            <h2 className="text-xl font-bold text-stone-800">APCNF Traceability Portal</h2>
            <p className="text-stone-500 max-w-xs mx-auto">
              The traceability portal opens in a secure in-app window for the best mobile experience.
            </p>
            <button 
              onClick={handleOpenLink}
              disabled={isOpeningInApp}
              className="bg-[#1b5e20] text-white px-10 py-4 rounded-2xl font-bold shadow-lg shadow-emerald-900/20 active:scale-95 transition-all flex items-center gap-2 mx-auto disabled:opacity-50"
            >
              {isOpeningInApp ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <ExternalLink size={20} />
              )}
              {isOpeningInApp ? 'Opening Portal...' : 'Open Traceability Portal'}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="w-24 h-24 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-2">
              <AlertTriangle size={48} className="text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-stone-800">Security Restriction</h2>
            <p className="text-stone-500 max-w-xs mx-auto">
              The traceability portal cannot be embedded directly due to security policies. Please click below to open it in a new tab.
            </p>
            <button 
              onClick={handleOpenLink}
              className="bg-[#1b5e20] text-white px-10 py-4 rounded-2xl font-bold shadow-lg shadow-emerald-900/20 active:scale-95 transition-all flex items-center gap-2 mx-auto"
            >
              <ExternalLink size={20} />
              Open in New Tab
            </button>
          </div>
        )}
      </div>
      
      {/* Visual Footer/Status Indicator */}
      <div className="bg-stone-50 px-4 py-2 border-t border-stone-200 flex justify-between items-center text-[10px] text-stone-400 font-medium">
        <span>{url}</span>
        <span className="flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
          Secure Portal
        </span>
      </div>
    </div>
  );
};

export default TraceabilityScreen;
